import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getAuth } from '@angular/fire/auth';
import { NzI18nInterface, en_US, es_ES, pt_BR } from 'ng-zorro-antd/i18n';
import { firstValueFrom } from 'rxjs';
import { EnumLanguage } from '../../enums/language.enum';
import { BrowserStorage } from '../../utils/browserStorage';
import { UserService } from '../user/user.service';
import { SpinnerService } from '../spinner/spinner.service';
import { getCurrentLanguage, LANGUAGE_STORAGE_KEY } from '../../utils/language.utils';



@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translateService = inject(TranslateService);
  private readonly userService = inject(UserService);
  private readonly spinnerService = inject(SpinnerService);

  async initialize(): Promise<void> {
    try {
      await firstValueFrom(this.translateService.use(this.getCurrentLanguage()));
    } catch (error) {
      console.error('Error loading translations:', error);
    }
  }

  getCurrentLanguage(): EnumLanguage {
    return getCurrentLanguage();
  }

  getLocaleId(): string {
    const currentLanguage = this.getCurrentLanguage();
    if (currentLanguage === EnumLanguage.ENGLISH) {
      return 'en-US';
    }
    if (currentLanguage === EnumLanguage.PORTUGUESE) {
      return 'pt-BR';
    }
    return 'es-UY';
  }

  getNzLocale(): NzI18nInterface {
    const currentLanguage = this.getCurrentLanguage();
    if (currentLanguage === EnumLanguage.ENGLISH) {
      return en_US;
    }
    if (currentLanguage === EnumLanguage.PORTUGUESE) {
      return pt_BR;
    }
    return es_ES;
  }

  async switchLanguage(language: EnumLanguage): Promise<void> {
    const previousLanguage = this.getCurrentLanguage();
    BrowserStorage.set(LANGUAGE_STORAGE_KEY, language);
    if (language === previousLanguage) {
      return;
    }
    // Full-view loader from the click until the page reloads; after the reload the
    // static loader in index.html takes over until the app finishes bootstrapping.
    this.spinnerService.show();
    await this.persistLanguageForCurrentUser(language);
    window.location.reload();
  }

  private async persistLanguageForCurrentUser(language: EnumLanguage): Promise<void> {
    if (!getAuth().currentUser) {
      return;
    }
    try {
      await this.userService.updateLanguage(language);
    } catch (error) {
      console.error('Error updating user language:', error);
    }
  }

}
